import {
  COIN,
  EXTRA_LIFE,
  GAME,
  type Bird,
  type Coin,
  type Phase,
  type Pipe,
} from './constants';
import { SoundManager } from './sound';

const START_SPEED = 2.2;
const MAX_SPEED = 7.5;
const SPEED_RAMP = 0.055;    // per passed pipe
const GAP_SHRINK_PER_PIPE = 0.7; // per passed pipe
const MIN_GAP = 130;

export interface EngineCallbacks {
  onScore: (score: number) => void;
  onPhase: (phase: Phase) => void;
  onGroundHit?: () => void;
  /** Fired whenever the coin balance or next-life cost changes. */
  onCoins?: (coins: number, nextLifeCost: number) => void;
  /** Fired when a death was paid off with coins and the bird revived. */
  onRevive?: () => void;
}

/** Core loop for the free canvas-based Flappy Bird clone. Owns the
 *  simulation state, runs its own rAF loop, and paints everything.
 *  The playfield fills the whole canvas: all metrics are derived from
 *  a unit scale based on the current height, so a resize just works. */
export class FlappyEngine {
  width: number = GAME.width;
  height: number = GAME.height;

  private ctx: CanvasRenderingContext2D;
  private cb: EngineCallbacks;
  private raf = 0;
  private lastTime = 0;
  private running = false;

  phase: Phase = 'ready';
  score = 0;

  /** Coins collected this run — the currency that pays for extra lives.
   *  Deliberately per-run: every new game starts from zero. */
  coins = 0;
  /** Extra lives used this run; drives the rising cost. */
  livesUsed = 0;

  /** Cost of the next revival: 5 → 8 → 11 → 14 … */
  get nextLifeCost(): number {
    return EXTRA_LIFE.baseCost + EXTRA_LIFE.costStep * this.livesUsed;
  }

  private bird: Bird = { y: GAME.height / 2 - 60, vy: 0, tilt: 0 };
  private pipes: Pipe[] = [];
  private coinEntities: Coin[] = [];
  private sparkles: { x: number; y: number; vx: number; vy: number; life: number }[] = [];
  private invincibleUntil = 0;
  private groundOffset = 0;
  private distance = 0;
  private pipeTimer = 0;
  private speed = START_SPEED;
  private hitPipe = false;
  private flashTimer = 0;
  private shake = 0;

  // Scaled metrics (recomputed on every resize)
  private u = 1;
  private birdX: number = GAME.birdX;
  private birdRadius: number = GAME.birdRadius;
  private pipeWidth: number = GAME.pipeWidth;
  private groundHeight: number = GAME.groundHeight;
  private spacing: number = GAME.pipeSpacing;
  private gravity: number = GAME.gravity;
  private flapVelocity: number = GAME.flapVelocity;
  private maxFall: number = GAME.maxFall;
  private nextGap: number = GAME.pipeGap;

  // Visual "randomized" background hills, fixed per session
  private hills = Array.from({ length: 7 }, () => ({
    x: Math.random(),
    s: 0.6 + Math.random() * 0.7,
  }));

  constructor(ctx: CanvasRenderingContext2D, cb: EngineCallbacks) {
    this.ctx = ctx;
    this.cb = cb;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.raf = requestAnimationFrame(this.tick);
  }

  destroy(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  /** Update the playfield size and rescaled every metric to match. */
  resize(width: number, height: number): void {
    this.width = Math.max(200, width);
    this.height = Math.max(300, height);
    this.u = this.height / GAME.height;

    this.birdX = this.width * 0.24;
    this.birdRadius = GAME.birdRadius * this.u;
    this.pipeWidth = GAME.pipeWidth * this.u;
    this.groundHeight = GAME.groundHeight * this.u;
    this.spacing = GAME.pipeSpacing * this.u;
    this.gravity = GAME.gravity * this.u;
    this.flapVelocity = GAME.flapVelocity * this.u;
    this.maxFall = GAME.maxFall * this.u;
    this.nextGap = GAME.pipeGap * this.u;

    // Keep the bird inside the new playfield
    if (this.bird.y < this.birdRadius) this.bird.y = this.birdRadius;
  }

  /** Reset every bit of simulation state and return to the ready phase.
   *  Coins and the extra-life price progression restart from scratch. */
  reset(): void {
    this.phase = 'ready';
    this.score = 0;
    this.coins = 0;
    this.livesUsed = 0;
    this.bird = { y: this.height / 2 - 60, vy: 0, tilt: 0 };
    this.pipes = [];
    this.coinEntities = [];
    this.sparkles = [];
    this.invincibleUntil = 0;
    this.groundOffset = 0;
    this.distance = 0;
    this.pipeTimer = 0;
    this.nextGap = GAME.pipeGap * this.u;
    this.speed = START_SPEED;
    this.hitPipe = false;
    this.flashTimer = 0;
    this.shake = 0;
    this.cb.onPhase('ready');
    this.cb.onScore(0);
    this.cb.onCoins?.(this.coins, this.nextLifeCost);
  }

  /** Space / tap handler. Flap while airborne, otherwise (re)start. */
  press(): void {
    if (this.phase === 'ready') {
      this.phase = 'playing';
      this.cb.onPhase('playing');
      this.flap();
    } else if (this.phase === 'playing') {
      this.flap();
    } else if (this.phase === 'gameover') {
      // Small delay guard so a frantic tap-right-after-death doesn't insta-restart
      if (performance.now() - this.deathAt > 350) {
        this.reset();
      }
    }
  }

  private deathAt = 0;

  private flap(): void {
    this.bird.vy = this.flapVelocity;
    SoundManager.play('flap');
  }

  private spawnPipe(): void {
    const margin = 56 * this.u;
    const max = this.height - this.groundHeight - margin - this.nextGap;
    const gapY = margin + Math.random() * Math.max(0, max - margin);
    this.pipes.push({ x: this.width + 12, gapY, passed: false });

    // Drop a coin halfway to the next pipe — that column is always open air,
    // but grabbing it can pull the bird off the safe gap path (risk/reward).
    if (Math.random() < COIN.spawnChance) {
      const minY = margin;
      const maxY = this.height - this.groundHeight - margin;
      const y = minY + Math.random() * Math.max(0, maxY - minY);
      this.coinEntities.push({ x: this.width + 12 + this.spacing * 0.5, y, taken: false });
    }
  }

  private tick = (time: number): void => {
    if (!this.running) return;
    const dt = Math.min((time - this.lastTime) / 1000, 0.05);
    this.lastTime = time;

    this.update(dt);
    this.draw();
    this.raf = requestAnimationFrame(this.tick);
  };

  private update(dt: number): void {
    // Scene always scrolls for a sense of motion
    this.groundOffset = (this.groundOffset + this.speed * this.u * dt * 60) % 24;
    this.distance += this.speed * this.u * dt * 60;
    this.shake = Math.max(0, this.shake - dt * 2);
    this.flashTimer = Math.max(0, this.flashTimer - dt);

    if (this.phase === 'ready') {
      // Gentle hover bob while waiting
      this.bird.y += Math.sin(timeNow() / 400) * 0.5;
      this.bird.tilt = 0;
      return;
    }

    if (this.phase === 'gameover') {
      // Bird keeps falling to the ground after death
      this.applyGravity(dt);
      return;
    }

    // playing
    this.applyGravity(dt);

    // Spawn pipes on a distance-based timer so all of them move together.
    // Pipes advance `speed * u * dt * 60` px per frame, so the timer must
    // accumulate in the same units as `spacing` (pixels).
    this.pipeTimer += this.speed * this.u * dt * 60;
    if (this.pipeTimer >= this.spacing) {
      this.pipeTimer = 0;
      this.spawnPipe();
    }

    // Move pipes, score on pass.
    for (const p of this.pipes) {
      p.x -= this.speed * this.u * dt * 60;
      if (!p.passed && p.x + this.pipeWidth < this.birdX) {
        p.passed = true;
        this.scorePassed();
      }
    }
    this.pipes = this.pipes.filter((p) => p.x + this.pipeWidth > -20);

    // Move coins, collect overlaps, decay sparkles.
    const dx = this.speed * this.u * dt * 60;
    for (const c of this.coinEntities) c.x -= dx;
    this.coinEntities = this.coinEntities.filter((c) => !c.taken && c.x > -20);
    this.collectCoins();
    this.updateSparkles(dt);

    this.checkCollisions();
  }

  /** Collect any coin the bird touches. */
  private collectCoins(): void {
    const reach = (GAME.birdRadius + COIN.radius) * this.u * 0.9;
    let collected = false;
    for (const c of this.coinEntities) {
      if (c.taken) continue;
      if (Math.hypot(c.x - this.birdX, c.y - this.bird.y) < reach) {
        c.taken = true;
        collected = true;
        this.coins += 1;
        SoundManager.play('coin');
        this.burst(c.x, c.y);
      }
    }
    if (collected) {
      this.cb.onCoins?.(this.coins, this.nextLifeCost);
    }
  }

  private burst(x: number, y: number): void {
    for (let i = 0; i < 8; i++) {
      const a = (Math.PI * 2 * i) / 8 + Math.random() * 0.5;
      const sp = (60 + Math.random() * 90) * this.u;
      this.sparkles.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 0.45 });
    }
  }

  private updateSparkles(dt: number): void {
    for (const s of this.sparkles) {
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.life -= dt;
    }
    this.sparkles = this.sparkles.filter((s) => s.life > 0);
  }

  private scorePassed(): void {
    this.score += 1;
    this.cb.onScore(this.score);
    SoundManager.play('score');
    this.speed = Math.min(MAX_SPEED, START_SPEED + this.score * SPEED_RAMP);
    this.nextGap = Math.max(
      MIN_GAP * this.u,
      GAME.pipeGap * this.u - this.score * GAP_SHRINK_PER_PIPE * this.u,
    );
  }

  private applyGravity(dt: number): void {
    this.bird.vy = Math.min(this.maxFall, this.bird.vy + this.gravity * dt * 60);
    this.bird.y += this.bird.vy * dt * 60;
    // Tilt lerps toward a target based on vertical velocity
    const targetTilt = Math.max(-0.35, Math.min(1.25, this.bird.vy / (6 * this.u)));
    this.bird.tilt += (targetTilt - this.bird.tilt) * Math.min(1, dt * 12);

    const ceiling = GAME.ceiling + this.birdRadius * 0.5;
    if (this.bird.y <= ceiling) {
      this.bird.y = ceiling;
      this.bird.vy = 0;
    }
  }

  private checkCollisions(): void {
    const invincible = timeNow() < this.invincibleUntil;
    const groundY = this.height - this.groundHeight;
    if (this.bird.y + this.birdRadius >= groundY) {
      if (invincible) {
        // Bounce off the ground instead of dying while shielded.
        this.bird.y = groundY - this.birdRadius - 1;
        this.bird.vy = this.flapVelocity * 0.85;
      } else {
        this.die();
        this.cb.onGroundHit?.();
      }
      return;
    }

    if (invincible) return; // pipes are ignored while shielded

    for (const p of this.pipes) {
      const inPipeX =
        p.x - this.birdRadius < this.birdX + this.birdRadius &&
        p.x + this.pipeWidth + this.birdRadius > this.birdX - this.birdRadius;
      if (!inPipeX) continue;
      const headAboveGap = this.bird.y - this.birdRadius <= p.gapY;
      const tailBelowGap = this.bird.y + this.birdRadius >= p.gapY + this.nextGap;
      if (headAboveGap || tailBelowGap) {
        this.die();
        return;
      }
    }
  }

  private die(): void {
    // Affordable death? Spend the coins and come back to life.
    if (this.coins >= this.nextLifeCost) {
      this.revive();
      return;
    }
    this.phase = 'gameover';
    this.deathAt = performance.now();
    this.shake = 0.45;
    this.flashTimer = 0.12;
    SoundManager.play('hit');
    SoundManager.play('whoosh');
    window.setTimeout(() => SoundManager.play('game_over'), 300);
    this.cb.onPhase('gameover');
  }

  /** Pay the doubling coin cost and keep flying with a short shield. */
  private revive(): void {
    this.coins -= this.nextLifeCost;
    this.livesUsed += 1;

    // Pop the bird upward and give it breathing room.
    this.bird.vy = this.flapVelocity;
    this.bird.tilt = -0.3;
    this.invincibleUntil = timeNow() + EXTRA_LIFE.invincibleMs;
    this.pipes = this.pipes.filter(
      (p) => p.x + this.pipeWidth < this.birdX - 12 || p.x > this.birdX + this.spacing * 0.8,
    );

    this.shake = 0.3;
    this.flashTimer = 0.18;
    SoundManager.play('hit');
    SoundManager.play('revive');
    this.cb.onCoins?.(this.coins, this.nextLifeCost);
    this.cb.onRevive?.();
  }

  // ── Rendering ───────────────────────────────────────────────────────────────

  draw(): void {
    const ctx = this.ctx;
    ctx.save();
    ctx.clearRect(0, 0, this.width, this.height);

    if (this.shake > 0) {
      ctx.translate(
        (Math.random() - 0.5) * this.shake * 14,
        (Math.random() - 0.5) * this.shake * 10,
      );
    }

    this.drawBackground(ctx);
    this.drawCoins(ctx);
    this.drawPipes(ctx);
    this.drawSparkles(ctx);
    this.drawBird(ctx);
    this.drawGround(ctx);

    if (this.flashTimer > 0) {
      ctx.fillStyle = `rgba(255,255,255,${this.flashTimer * 4})`;
      ctx.fillRect(-20, -20, this.width + 40, this.height + 40);
    }

    ctx.restore();
  }

  private drawBackground(ctx: CanvasRenderingContext2D): void {
    const grad = ctx.createLinearGradient(0, 0, 0, this.height);
    grad.addColorStop(0, '#4ec0ca');
    grad.addColorStop(1, '#9fe7e7');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, this.width, this.height);

    // Distant clouds
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    for (let i = 0; i < 3; i++) {
      const cx = ((i * 130 + this.distance * 0.2) % (this.width + 120)) - 60;
      const cy = 40 * this.u + i * 55 * this.u;
      cloud(ctx, cx, cy, 1 * this.u);
    }

    // Rolling hills
    for (const h of this.hills) {
      const speed = 0.15 + h.s * 0.2;
      const offset = (this.distance * speed) % (this.width + 160);
      const y = this.height - this.groundHeight - 14 * this.u;
      const wMul = 0.5 + h.s;
      const hMul = (0.5 + h.s) * this.u;
      ctx.fillStyle = h.x > 0.5 ? 'rgba(94,186,135,0.28)' : 'rgba(114,201,150,0.25)';
      ctx.beginPath();
      ctx.ellipse(offset - 80, y, 110 * wMul, 44 * hMul, 0, Math.PI, 0);
      ctx.fill();
    }
  }

  private drawPipes(ctx: CanvasRenderingContext2D): void {
    for (const p of this.pipes) {
      const topH = p.gapY;
      const bottomY = p.gapY + this.nextGap;
      const x = p.x;

      // Top pipe
      this.drawPipeBody(ctx, x, 0, this.pipeWidth, topH);
      this.drawPipeLid(ctx, x, topH, 1);
      // Bottom pipe
      this.drawPipeBody(ctx, x, bottomY, this.pipeWidth, this.height - this.groundHeight - bottomY);
      this.drawPipeLid(ctx, x, bottomY, -1);
    }
  }

  private drawPipeBody(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
  ): void {
    if (h <= 0) return;
    const grad = ctx.createLinearGradient(x, 0, x + w, 0);
    grad.addColorStop(0, '#5b8f3b');
    grad.addColorStop(0.45, '#7fb844');
    grad.addColorStop(1, '#4a7430');
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = 'rgba(255,255,255,0.14)';
    ctx.fillRect(x + 7 * this.u, y, 8 * this.u, h);
    ctx.strokeStyle = 'rgba(28,50,20,0.55)';
    ctx.lineWidth = 2 * this.u;
    ctx.strokeRect(x + 1 * this.u, y + 1 * this.u, w - 2 * this.u, h - 2 * this.u);
  }

  private drawPipeLid(ctx: CanvasRenderingContext2D, x: number, y: number, dir: number): void {
    const w = this.pipeWidth + 8 * this.u;
    const h = 26 * this.u;
    const yy = dir === 1 ? y - h : y;
    const grad = ctx.createLinearGradient(x - 4 * this.u, 0, x + w - 4 * this.u, 0);
    grad.addColorStop(0, '#5b8f3b');
    grad.addColorStop(0.45, '#7fb844');
    grad.addColorStop(1, '#4a7430');
    ctx.fillStyle = grad;
    ctx.fillRect(x - 4 * this.u, yy, w, h);
    ctx.strokeStyle = 'rgba(28,50,20,0.55)';
    ctx.lineWidth = 2 * this.u;
    ctx.strokeRect(x - 3 * this.u, yy + 1 * this.u, w - 2 * this.u, h - 2 * this.u);
  }

  private drawGround(ctx: CanvasRenderingContext2D): void {
    const gy = this.height - this.groundHeight;
    ctx.fillStyle = '#ded895';
    ctx.fillRect(0, gy, this.width, this.groundHeight);
    ctx.fillStyle = '#eae3a0';
    ctx.fillRect(0, gy, this.width, 6 * this.u);
    // Scrolling grass tufts anchored at the top edge of the ground.
    ctx.strokeStyle = 'rgba(125,171,63,0.5)';
    ctx.lineWidth = 2.2 * this.u;
    ctx.lineCap = 'round';
    ctx.beginPath();
    const step = 24 * this.u;
    for (let x = -2 * step; x <= this.width + 2 * step; x += step) {
      const sx = x - this.groundOffset;
      // A short blade leans from the grass line down into the dirt.
      ctx.moveTo(sx, gy + 1 * this.u);
      ctx.lineTo(sx - 12 * this.u, gy + 12 * this.u);
    }
    ctx.stroke();

    // Dirt divider line under the grass so the "land" reads clearly.
    ctx.strokeStyle = 'rgba(140,120,50,0.35)';
    ctx.lineWidth = 2 * this.u;
    ctx.beginPath();
    ctx.moveTo(0, gy + 14 * this.u);
    ctx.lineTo(this.width, gy + 14 * this.u);
    ctx.stroke();
  }

  /** Spinning golden coins with a gentle vertical bob. */
  private drawCoins(ctx: CanvasRenderingContext2D): void {
    for (const c of this.coinEntities) {
      const t = timeNow();
      // Never squash below 55% width — thinner than that reads as a grey
      // crescent instead of a spinning coin.
      const spin = 0.55 + 0.45 * Math.abs(Math.sin(t / 260 + c.x * 0.008));
      const bob = Math.sin(t / 300 + c.x * 0.02) * 3 * this.u;
      const r = COIN.radius * this.u;

      ctx.save();
      ctx.translate(c.x, c.y + bob);
      ctx.scale(spin, 1);

      // Dark gold rim
      ctx.fillStyle = '#b8860b';
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fill();

      // Bright gold face with soft depth
      const grad = ctx.createRadialGradient(-r * 0.3, -r * 0.35, r * 0.15, 0, 0, r);
      grad.addColorStop(0, '#fff3b0');
      grad.addColorStop(0.5, '#ffd700');
      grad.addColorStop(1, '#f0a500');
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.82, 0, Math.PI * 2);
      ctx.fill();

      // Glint
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.beginPath();
      ctx.arc(-r * 0.32, -r * 0.36, r * 0.16, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    }
  }

  private drawSparkles(ctx: CanvasRenderingContext2D): void {
    for (const s of this.sparkles) {
      ctx.globalAlpha = Math.max(0, s.life / 0.45);
      ctx.fillStyle = '#ffe9a3';
      ctx.beginPath();
      ctx.arc(s.x, s.y, 2.6 * this.u * (s.life / 0.45), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  private drawBird(ctx: CanvasRenderingContext2D): void {
    const { bird } = this;
    const u = this.u;
    const pulse = 1 + Math.sin(timeNow() / 160) * 0.04;
    const invincible = timeNow() < this.invincibleUntil;

    // Soft golden glow while the extra-life invincibility lasts —
    // a breathing halo of light instead of a hard ring.
    if (invincible) {
      const t = timeNow();
      const breathe = 1 + Math.sin(t / 180) * 0.08;
      const r = (GAME.birdRadius + 16) * u * breathe;
      // Fade the glow out over the last 300ms of the shield.
      const fade = Math.min(1, Math.max(0, (this.invincibleUntil - t) / 300));
      ctx.save();
      const glow = ctx.createRadialGradient(
        this.birdX,
        bird.y,
        GAME.birdRadius * 0.4 * u,
        this.birdX,
        bird.y,
        r,
      );
      glow.addColorStop(0, `rgba(255, 214, 102, ${0.5 * fade})`);
      glow.addColorStop(0.6, `rgba(255, 196, 60, ${0.25 * fade})`);
      glow.addColorStop(1, 'rgba(255, 196, 60, 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(this.birdX, bird.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    ctx.save();
    ctx.translate(this.birdX, bird.y);
    ctx.rotate(bird.tilt);
    // Blink while shielded so the state is obvious.
    if (invincible && Math.floor(timeNow() / 90) % 2 === 0) ctx.globalAlpha = 0.4;
    ctx.scale(pulse * u, pulse * u);

    // Wing (drawn behind body)
    const wingUp = this.phase === 'ready' && Math.sin(timeNow() / 200) > 0.3;
    this.drawWing(ctx, wingUp);

    // Body
    const grad = ctx.createLinearGradient(0, -14, 0, 14);
    grad.addColorStop(0, '#ffe28a');
    grad.addColorStop(1, '#f2a93b');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(0, 0, 15, 13, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#8a5a1e';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Belly
    ctx.fillStyle = '#ffd98a';
    ctx.beginPath();
    ctx.ellipse(3, 6, 9, 6, 0, 0, Math.PI * 2);
    ctx.fill();

    // Eye
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(8, -5, 4.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#1c1c1c';
    ctx.beginPath();
    ctx.arc(9.5, -5.5, 2.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(10.2, -6.2, 0.9, 0, Math.PI * 2);
    ctx.fill();

    // Wing shadow layer
    ctx.fillStyle = 'rgba(0,0,0,0.08)';
    ctx.beginPath();
    ctx.ellipse(-3, 2, 10, 7, 0.2, 0, Math.PI * 2);
    ctx.fill();

    // Beak
    ctx.fillStyle = '#f07b2d';
    ctx.beginPath();
    ctx.moveTo(14, 1);
    ctx.lineTo(24, 3.5);
    ctx.lineTo(14, 7);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#a34f1a';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    ctx.restore();
  }

  private drawWing(ctx: CanvasRenderingContext2D, up: boolean): void {
    const rot = up ? -0.5 : 0.5;
    ctx.save();
    ctx.translate(-4, 1);
    ctx.rotate(rot + (this.phase === 'playing' ? Math.sin(timeNow() / 70) * 0.45 : 0));
    ctx.fillStyle = '#fff3cf';
    ctx.beginPath();
    ctx.ellipse(-7, 2, 9, 5.5, 0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#e6b65e';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();
  }
}

function cloud(ctx: CanvasRenderingContext2D, x: number, y: number, s: number): void {
  ctx.beginPath();
  ctx.arc(x, y, 14 * s, 0, Math.PI * 2);
  ctx.arc(x + 16 * s, y - 6 * s, 18 * s, 0, Math.PI * 2);
  ctx.arc(x + 34 * s, y, 13 * s, 0, Math.PI * 2);
  ctx.fill();
}

export function timeNow(): number {
  return performance.now();
}

/** Best score persisted across sessions. */
export const bestScore = {
  load(): number {
    try {
      return Number(window.localStorage.getItem('flappy-bird-best') || 0) || 0;
    } catch {
      return 0;
    }
  },
  save(score: number): void {
    try {
      window.localStorage.setItem('flappy-bird-best', String(score));
    } catch {
      /* ignore */
    }
  },
};